import {
  OrganizationManager,
  Opportunity,
  type PartnerOrganization,
  type User,
} from "@entities";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  opportunityRepo,
  organizationManagerRepo,
  partnerOrganizationManagerRepo,
  partnerOrganizationRepo,
  userRepo,
} from "@repositories";
import { PARTNER_MANAGER_PHONE_LOCKED } from "@shared/entities/user/config";
import { eligiblePartnerOrganizationParents } from "@shared/helpers/partner-organization-hierarchy";
import { normalizePhoneDigitsForSearch } from "@shared/helpers/normalize-phone-for-search";
import {
  assignExistingUserAsPartnerOrganizationAppManager,
  formatPhoneForPartnerManager,
  linkUserAsPartnerOrganizationAppManager,
  removePartnerManagerAssociationRole,
} from "@shared/helpers/partner-organization-manager";
import { Roles } from "@shared/Roles";
import { remult } from "remult";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UploadImageHandle } from "../../../components/form-components/image-uploader/ImageUploader";
import MapPinDialog from "../../../components/mission-map/MapPinDialog";
import { PartnerOrganizationAddressesEditor } from "./PartnerOrganizationAddressesEditor";
import { PartnerOrganizationHandlingFields } from "./PartnerOrganizationHandlingFields";
import { PartnerOrganizationManagersPanel } from "./PartnerOrganizationManagersPanel";
import { PartnerOrganizationOpportunitiesPanel } from "./PartnerOrganizationOpportunitiesPanel";
import { PartnerOrganizationProfileFields } from "./PartnerOrganizationProfileFields";
import { loadPartnerOrganizationForDetails } from "./partner-organization-details.helper";
import {
  emptyPartnerOrganizationFormValues,
  type PartnerOrganizationFormValues,
} from "./partner-organization-form";
import {
  draftsFromPartnerOrganization,
  primaryAddressFromDrafts,
  syncPartnerOrganizationAddresses,
  type PartnerOrgAddressDraft,
} from "./partner-organization-addresses";

export { COOPERATION_LEVEL_OPTIONS } from "./cooperation-level";
export type { PartnerOrganizationFormValues };

export type PartnerOrganizationSavedResult = {
  organization: PartnerOrganization;
  close: boolean;
  created: boolean;
};

type EditTab = "profile" | "addresses" | "handling" | "managers" | "opportunities";

const CREATE_STEPS = ["פרופיל וכתובות", "טיפול ודירוג", "מנהלים ומשימות"] as const;

const EDIT_TABS: {
  value: EditTab;
  label: string;
  icon: typeof BusinessOutlinedIcon;
}[] = [
  { value: "profile", label: "פרופיל", icon: BusinessOutlinedIcon },
  { value: "addresses", label: "כתובות", icon: PlaceOutlinedIcon },
  { value: "handling", label: "טיפול", icon: SupportAgentOutlinedIcon },
  { value: "managers", label: "מנהלים", icon: PeopleOutlinedIcon },
  { value: "opportunities", label: "משימות", icon: AssignmentOutlinedIcon },
];

interface PartnerOrganizationDialogProps {
  open: boolean;
  organization: PartnerOrganization | null;
  createDefaults?: Partial<PartnerOrganizationFormValues> & {
    appManagerPhone?: string;
    appManagerEmail?: string;
    appManagerRole?: string;
    parentName?: string;
  };
  onClose: () => void;
  onSaved: (result: PartnerOrganizationSavedResult) => void | Promise<void>;
}

function valuesFromOrganization(
  organization: PartnerOrganization,
): PartnerOrganizationFormValues {
  return {
    name: organization.name ?? "",
    description: organization.description ?? "",
    comments: organization.comments ?? "",
    acceptsGroups: organization.acceptsGroups ?? false,
    acceptsEnglishSpeakers: organization.acceptsEnglishSpeakers ?? false,
    logoImageId: (organization.logoImageId || "").trim(),
    maxGroupSize:
      organization.maxGroupSize != null
        ? String(organization.maxGroupSize)
        : "",
    operatingHours: organization.operatingHours ?? "",
    isAccessible: organization.isAccessible ?? false,
    cooperationLevel:
      organization.cooperationLevel != null
        ? organization.cooperationLevel
        : "",
    dailyVolunteerTarget:
      organization.dailyVolunteerTarget != null
        ? String(organization.dailyVolunteerTarget)
        : "",
    handledBy: organization.handledBy ?? "",
    handlingManagerId: organization.handlingManagerId ?? "",
    nextFollowUpAt: organization.nextFollowUpAt
      ? dayjs(organization.nextFollowUpAt).format("YYYY-MM-DD")
      : "",
    parentId: organization.parentId ?? "",
  };
}

export function PartnerOrganizationDialog({
  open,
  organization,
  createDefaults,
  onClose,
  onSaved,
}: PartnerOrganizationDialogProps) {
  const [flow, setFlow] = useState<"wizard" | "tabs">("tabs");
  const [createStep, setCreateStep] = useState(0);
  const [editTab, setEditTab] = useState<EditTab>("profile");
  const [values, setValues] = useState<PartnerOrganizationFormValues>(
    emptyPartnerOrganizationFormValues,
  );
  const [appManagers, setAppManagers] = useState<User[]>([]);
  const [internalManagers, setInternalManagers] = useState<OrganizationManager[]>(
    [],
  );
  const [linkedOpportunities, setLinkedOpportunities] = useState<Opportunity[]>(
    [],
  );
  const [managerPhone, setManagerPhone] = useState("");
  const [orgManagerSearch, setOrgManagerSearch] = useState("");
  const [orgManagerOptions, setOrgManagerOptions] = useState<
    OrganizationManager[]
  >([]);
  const [selectedOrgManager, setSelectedOrgManager] =
    useState<OrganizationManager | null>(null);
  const [responsibleManager, setResponsibleManager] =
    useState<OrganizationManager | null>(null);
  const [responsibleManagerSearch, setResponsibleManagerSearch] = useState("");
  const [responsibleManagerOptions, setResponsibleManagerOptions] = useState<
    OrganizationManager[]
  >([]);
  const [newManagerName, setNewManagerName] = useState("");
  const [newManagerEmail, setNewManagerEmail] = useState("");
  const [newManagerRole, setNewManagerRole] = useState("");
  const [newManagerPhone, setNewManagerPhone] = useState("");
  const [opportunitySearch, setOpportunitySearch] = useState("");
  const [opportunityOptions, setOpportunityOptions] = useState<Opportunity[]>(
    [],
  );
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<Opportunity | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [addressDrafts, setAddressDrafts] = useState<PartnerOrgAddressDraft[]>(
    () => draftsFromPartnerOrganization(null),
  );
  const [mapAddressClientId, setMapAddressClientId] = useState<string | null>(
    null,
  );
  const logoUploadRef = useRef<UploadImageHandle>(null);
  const lastHydratedOrgId = useRef<string | null>(null);
  const sessionCreatedRef = useRef(false);
  const [workingOrgId, setWorkingOrgId] = useState<string | undefined>();

  const savedId = workingOrgId ?? organization?.id;

  const { data: hierarchyRows = [] } = useQuery({
    queryKey: ["partner-organization-parent-options"],
    enabled: open,
    queryFn: () =>
      partnerOrganizationRepo.find({
        orderBy: { name: "asc" },
        limit: 5000,
      }),
  });
  const parentOptions = useMemo(() => {
    const links = hierarchyRows.map((row) => ({
      id: row.id,
      parentId: row.parentId ?? null,
    }));
    const allowed = new Set(
      eligiblePartnerOrganizationParents(organization?.id ?? null, links).map(
        (row) => row.id,
      ),
    );
    return hierarchyRows
      .filter((row) => allowed.has(row.id))
      .map((row) => ({ id: row.id, name: row.name }));
  }, [hierarchyRows, organization?.id]);
  const childNames = hierarchyRows
    .filter((row) => organization?.id && row.parentId === organization.id)
    .map((row) => row.name);

  const loadAppManagers = useCallback(async (partnerOrganizationId: string) => {
    const list = await userRepo.find({
      where: { partnerOrganizationId },
      orderBy: { name: "asc" },
    });
    setAppManagers(list);
  }, []);

  const loadInternalManagers = useCallback(
    async (partnerOrganizationId: string) => {
      const links = await partnerOrganizationManagerRepo.find({
        where: { partnerOrganizationId },
        include: { manager: { include: { organization: true } } },
      });
      setInternalManagers(
        links
          .map((link) => link.manager)
          .filter((m): m is OrganizationManager => !!m),
      );
    },
    [],
  );

  const searchOrganizationManagers = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setOrgManagerOptions([]);
      return;
    }
    const phoneQuery = normalizePhoneDigitsForSearch(q);
    const list = await organizationManagerRepo.find({
      where: OrganizationManager.hyperFilter({
        $or: [
          { name: { $contains: q } },
          { phoneNumber: { $contains: phoneQuery } },
        ],
      }),
      include: { organization: true },
      orderBy: { name: "asc" },
      limit: 25,
    });
    setOrgManagerOptions(list);
  }, []);

  const searchResponsibleManagers = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setResponsibleManagerOptions([]);
      return;
    }
    const phoneQuery = normalizePhoneDigitsForSearch(q);
    const list = await organizationManagerRepo.find({
      where: OrganizationManager.hyperFilter({
        $or: [
          { name: { $contains: q } },
          { phoneNumber: { $contains: phoneQuery } },
        ],
      }),
      include: { organization: true },
      orderBy: { name: "asc" },
      limit: 25,
    });
    setResponsibleManagerOptions(list);
  }, []);

  const loadOpportunities = useCallback(async (partnerOrganizationId: string) => {
    const list = await opportunityRepo.find({
      where: { partnerOrganizationId, parentOpportunityId: null },
      orderBy: { createdAt: "desc" },
    });
    setLinkedOpportunities(list);
  }, []);

  const searchOpportunities = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setOpportunityOptions([]);
      return;
    }
    const list = await opportunityRepo.find({
      where: Opportunity.hyperFilter({
        isDeleted: false,
        parentOpportunityId: null,
        title: { $contains: q },
      }),
      orderBy: { createdAt: "desc" },
      limit: 25,
    });
    setOpportunityOptions(list);
  }, []);

  const resetTransientFields = () => {
    setManagerPhone("");
    setOrgManagerSearch("");
    setSelectedOrgManager(null);
    setOrgManagerOptions([]);
    setResponsibleManagerSearch("");
    setNewManagerName("");
    setNewManagerPhone("");
    setNewManagerEmail("");
    setNewManagerRole("");
    setOpportunitySearch("");
    setSelectedOpportunity(null);
    setOpportunityOptions([]);
    setMapAddressClientId(null);
  };

  useEffect(() => {
    if (!open) {
      lastHydratedOrgId.current = null;
      sessionCreatedRef.current = false;
      setWorkingOrgId(undefined);
      return;
    }

    const orgId = organization?.id ?? null;
    const hydrationKey =
      orgId ??
      `create:${createDefaults?.parentId ?? ""}:${createDefaults?.name ?? ""}:${createDefaults?.handlingManagerId ?? ""}:${createDefaults?.appManagerPhone ?? ""}:${createDefaults?.appManagerEmail ?? ""}:${createDefaults?.appManagerRole ?? ""}`;
    if (lastHydratedOrgId.current === hydrationKey) return;

    lastHydratedOrgId.current = hydrationKey;
    setError(null);
    setSavedNotice(null);
    resetTransientFields();

    if (organization) {
      setWorkingOrgId(organization.id);
      setValues(valuesFromOrganization(organization));
      setResponsibleManager(organization.handlingManager ?? null);
      setResponsibleManagerOptions(
        organization.handlingManager ? [organization.handlingManager] : [],
      );
      if (!organization.handlingManager && organization.handlingManagerId) {
        void organizationManagerRepo
          .findId(organization.handlingManagerId)
          .then((manager) => {
            if (!manager) return;
            setResponsibleManager(manager);
            setResponsibleManagerOptions([manager]);
          });
      }
      setAddressDrafts(draftsFromPartnerOrganization(organization));
      void loadAppManagers(organization.id);
      void loadInternalManagers(organization.id);
      void loadOpportunities(organization.id);
      if (sessionCreatedRef.current) {
        setFlow("wizard");
        setCreateStep(2);
      } else {
        setFlow("tabs");
        setEditTab("profile");
      }
    } else {
      const { appManagerPhone, appManagerEmail, appManagerRole, parentName, ...formDefaults } =
        createDefaults ?? {};
      void parentName;
      const nextValues = {
        ...emptyPartnerOrganizationFormValues,
        ...formDefaults,
        name: createDefaults?.name ?? "",
        handlingManagerId: createDefaults?.handlingManagerId ?? "",
        comments: createDefaults?.comments ?? "",
      };
      setValues(nextValues);
      setAddressDrafts(draftsFromPartnerOrganization(null));
      setAppManagers([]);
      setInternalManagers([]);
      setLinkedOpportunities([]);
      setResponsibleManager(null);
      setResponsibleManagerOptions([]);
      setWorkingOrgId(undefined);
      setFlow("wizard");
      setCreateStep(0);
      setNewManagerPhone(appManagerPhone?.trim() ?? "");
      setNewManagerEmail(appManagerEmail?.trim() ?? "");
      setNewManagerRole(appManagerRole?.trim() ?? "");
      if (nextValues.handlingManagerId) {
        void organizationManagerRepo
          .findId(nextValues.handlingManagerId)
          .then((manager) => {
            if (!manager) return;
            setResponsibleManager(manager);
            setResponsibleManagerOptions([manager]);
          });
      }
    }
  }, [
    open,
    organization,
    createDefaults,
    loadAppManagers,
    loadInternalManagers,
    loadOpportunities,
  ]);

  useEffect(() => {
    const t = setTimeout(() => {
      void searchOpportunities(opportunitySearch);
    }, 300);
    return () => clearTimeout(t);
  }, [opportunitySearch, searchOpportunities]);

  useEffect(() => {
    const t = setTimeout(() => {
      void searchOrganizationManagers(orgManagerSearch);
    }, 300);
    return () => clearTimeout(t);
  }, [orgManagerSearch, searchOrganizationManagers]);

  useEffect(() => {
    const t = setTimeout(() => {
      void searchResponsibleManagers(responsibleManagerSearch);
    }, 300);
    return () => clearTimeout(t);
  }, [responsibleManagerSearch, searchResponsibleManagers]);

  const persistOrganization = async () => {
    if (!values.name.trim()) {
      setError("שם הארגון הוא שדה חובה");
      return null;
    }

    let logoImageId = values.logoImageId.trim();
    try {
      const uploadedLogoId = await logoUploadRef.current?.uploadPending();
      if (uploadedLogoId?.trim()) {
        logoImageId = uploadedLogoId.trim();
        setValues((current) => ({ ...current, logoImageId }));
      }
    } catch {
      setError("העלאת הלוגו נכשלה - נסו שוב");
      return null;
    }

    const maxGroupSizeRaw = values.maxGroupSize.trim();
    const maxGroupSize = maxGroupSizeRaw
      ? Number.parseInt(maxGroupSizeRaw, 10)
      : null;
    if (
      maxGroupSizeRaw &&
      (!Number.isFinite(maxGroupSize) || (maxGroupSize as number) < 1)
    ) {
      setError("מקסימום אנשים בקבוצה חייב להיות מספר חיובי");
      return null;
    }

    const dailyTargetRaw = values.dailyVolunteerTarget.trim();
    const dailyVolunteerTarget = dailyTargetRaw
      ? Number.parseInt(dailyTargetRaw, 10)
      : null;
    if (
      dailyTargetRaw &&
      (!Number.isFinite(dailyVolunteerTarget) ||
        (dailyVolunteerTarget as number) < 1)
    ) {
      setError("יעד מתנדבים יומי חייב להיות מספר חיובי, או ריק");
      return null;
    }

    const primary = primaryAddressFromDrafts(addressDrafts);
    const payload = {
      name: values.name.trim(),
      description: values.description.trim() || undefined,
      comments: values.comments.trim(),
      acceptsGroups: values.acceptsGroups,
      acceptsEnglishSpeakers: values.acceptsEnglishSpeakers,
      logoImageId,
      address: primary.address,
      coordinates: (primary.coordinates ?? null) as
        | { lat: number; lng: number }
        | undefined,
      maxGroupSize,
      operatingHours: values.operatingHours.trim() || undefined,
      isAccessible: values.isAccessible,
      handledBy: values.handledBy === "" ? null : values.handledBy,
      handlingManagerId:
        values.handlingManagerId.trim() === ""
          ? null
          : values.handlingManagerId.trim(),
      nextFollowUpAt: values.nextFollowUpAt
        ? dayjs(values.nextFollowUpAt).startOf("day").toDate()
        : null,
      cooperationLevel:
        values.cooperationLevel === "" ? null : values.cooperationLevel,
      dailyVolunteerTarget,
      parentId: values.parentId.trim() || null,
    };

    const isCreate = !savedId;
    let orgId = savedId;
    if (!orgId) {
      const created = await partnerOrganizationRepo.insert(payload);
      orgId = created.id;
    } else {
      await partnerOrganizationRepo.update(orgId, payload);
    }
    await syncPartnerOrganizationAddresses(orgId, addressDrafts);
    const loaded = await loadPartnerOrganizationForDetails(orgId);
    if (!loaded) {
      setError("שמירה נכשלה");
      return null;
    }

    lastHydratedOrgId.current = orgId;
    setWorkingOrgId(orgId);
    setAddressDrafts(draftsFromPartnerOrganization(loaded));
    return { organization: loaded, created: isCreate };
  };

  const handleSave = async (afterCreate: "continue" | "stay") => {
    setSaving(true);
    setError(null);
    setSavedNotice(null);
    try {
      const result = await persistOrganization();
      if (!result) return;
      if (result.created) {
        sessionCreatedRef.current = true;
        setCreateStep(2);
        void loadAppManagers(result.organization.id);
        void loadInternalManagers(result.organization.id);
        void loadOpportunities(result.organization.id);
      }
      await onSaved({
        organization: result.organization,
        close: false,
        created: result.created,
      });
      if (!result.created) {
        setSavedNotice("הארגון נשמר");
      }
      if (afterCreate === "continue" && result.created) {
        setFlow("wizard");
        setCreateStep(2);
      }
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "שמירה נכשלה";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const linkExistingManager = async () => {
    if (!savedId) {
      setError("יש לשמור את הארגון לפני שיוך מנהלים");
      return;
    }
    setError(null);
    const user = await userRepo.findFirst({
      phoneNumber: formatPhoneForPartnerManager(managerPhone.trim()),
    });
    if (!user) {
      setError("משתמש עם מספר זה לא נמצא - השתמש ב«מנהל חדש» ליצירה");
      return;
    }
    const err = await assignExistingUserAsPartnerOrganizationAppManager(
      user.id,
      savedId,
    );
    if (err) {
      setError(err);
      return;
    }
    setManagerPhone("");
    await loadAppManagers(savedId);
  };

  const linkInternalManager = async () => {
    if (!savedId) {
      setError("יש לשמור את הארגון לפני שיוך מנהלים");
      return;
    }
    if (!selectedOrgManager) return;
    if (internalManagers.some((m) => m.id === selectedOrgManager.id)) {
      setError("מנהל זה כבר משויך לארגון");
      return;
    }
    setError(null);
    await partnerOrganizationManagerRepo.insert({
      partnerOrganizationId: savedId,
      managerId: selectedOrgManager.id,
    });
    setSelectedOrgManager(null);
    setOrgManagerSearch("");
    await loadInternalManagers(savedId);
  };

  const removeInternalManager = async (manager: OrganizationManager) => {
    if (!savedId) return;
    await partnerOrganizationManagerRepo.delete({
      partnerOrganizationId: savedId,
      managerId: manager.id,
    });
    await loadInternalManagers(savedId);
  };

  const createManager = async () => {
    if (!savedId) {
      setError("יש לשמור את הארגון לפני יצירת מנהל");
      return;
    }
    setError(null);
    const err = await linkUserAsPartnerOrganizationAppManager(savedId, {
      name: newManagerName,
      phoneNumber: newManagerPhone,
      email: newManagerEmail,
      partnerContactRole: newManagerRole,
    });
    if (err) {
      setError(err);
      return;
    }
    setNewManagerName("");
    setNewManagerPhone("");
    setNewManagerEmail("");
    setNewManagerRole("");
    await loadAppManagers(savedId);
  };

  const removeAppManager = async (user: User) => {
    if (!savedId) return;
    await userRepo.update(user.id, { partnerOrganizationId: null });
    await userRepo.update(user.id, { partnerContactRole: null });
    await removePartnerManagerAssociationRole(user.id);
    await loadAppManagers(savedId);
  };

  const linkOpportunity = async () => {
    if (!savedId || !selectedOpportunity) return;
    setError(null);
    await opportunityRepo.update(selectedOpportunity.id, {
      partnerOrganizationId: savedId,
    });
    setSelectedOpportunity(null);
    setOpportunitySearch("");
    await loadOpportunities(savedId);
  };

  const unlinkOpportunity = async (opportunity: Opportunity) => {
    await opportunityRepo.update(opportunity.id, {
      partnerOrganizationId: null,
    });
    if (savedId) await loadOpportunities(savedId);
  };

  const flushLogoPending = async (): Promise<boolean> => {
    try {
      const uploadedLogoId = await logoUploadRef.current?.uploadPending();
      if (uploadedLogoId?.trim()) {
        const logoImageId = uploadedLogoId.trim();
        setValues((current) => ({ ...current, logoImageId }));
      }
      return true;
    } catch {
      setError("העלאת הלוגו נכשלה - נסו שוב");
      return false;
    }
  };

  const goNextFromProfile = async () => {
    if (!values.name.trim()) {
      setError("שם הארגון הוא שדה חובה");
      return;
    }
    setError(null);
    const uploaded = await flushLogoPending();
    if (!uploaded) return;
    setCreateStep(1);
  };

  const handleEditTabChange = async (nextTab: EditTab) => {
    if (editTab === "profile" && nextTab !== "profile") {
      const uploaded = await flushLogoPending();
      if (!uploaded) return;
    }
    setEditTab(nextTab);
  };

  const updateValues: typeof setValues = (action) => {
    setSavedNotice(null);
    setValues(action);
  };

  const updateAddressDrafts: typeof setAddressDrafts = (action) => {
    setSavedNotice(null);
    setAddressDrafts(action);
  };

  const profileBlock = (
    <PartnerOrganizationProfileFields
      values={values}
      onChange={updateValues}
      logoUploadRef={logoUploadRef}
      logoResetKey={`${organization?.id ?? "new"}-${open}`}
      parentOptions={parentOptions}
      currentParentName={
        organization?.parent?.name ||
        createDefaults?.parentName ||
        hierarchyRows.find((row) => row.id === values.parentId)?.name
      }
      childNames={childNames}
    />
  );

  const addressesBlock = (
    <PartnerOrganizationAddressesEditor
      drafts={addressDrafts}
      onChange={updateAddressDrafts}
      onPickOnMap={setMapAddressClientId}
    />
  );

  const handlingBlock = (
    <PartnerOrganizationHandlingFields
      values={values}
      onChange={updateValues}
      responsibleManager={responsibleManager}
      onResponsibleManagerChange={setResponsibleManager}
      responsibleManagerSearch={responsibleManagerSearch}
      onResponsibleManagerSearchChange={setResponsibleManagerSearch}
      responsibleManagerOptions={responsibleManagerOptions}
    />
  );

  const managersBlock = (
    <PartnerOrganizationManagersPanel
      orgManagerSearch={orgManagerSearch}
      onOrgManagerSearchChange={setOrgManagerSearch}
      orgManagerOptions={orgManagerOptions}
      selectedOrgManager={selectedOrgManager}
      onSelectedOrgManagerChange={setSelectedOrgManager}
      onLinkInternalManager={() => void linkInternalManager()}
      internalManagers={internalManagers}
      onRemoveInternalManager={(manager) => void removeInternalManager(manager)}
      managerPhone={managerPhone}
      onManagerPhoneChange={setManagerPhone}
      onLinkExistingManager={() => void linkExistingManager()}
      newManagerName={newManagerName}
      onNewManagerNameChange={setNewManagerName}
      newManagerPhone={newManagerPhone}
      onNewManagerPhoneChange={setNewManagerPhone}
      newManagerEmail={newManagerEmail}
      onNewManagerEmailChange={setNewManagerEmail}
      newManagerRole={newManagerRole}
      onNewManagerRoleChange={setNewManagerRole}
      onCreateManager={() => void createManager()}
      appManagers={appManagers}
      onRemoveAppManager={(user) => void removeAppManager(user)}
      onUpdateAppManager={(user, patch) => {
        const name = patch.name.trim();
        const phoneNumber = formatPhoneForPartnerManager(patch.phoneNumber.trim());
        const canEditPhone = remult.isAllowed(Roles.SuperAdmins);
        if (!name) {
          setError("יש להזין שם למנהל");
          return;
        }
        if (!phoneNumber || phoneNumber.length < 12) {
          setError("מספר טלפון לא תקין");
          return;
        }
        if (!canEditPhone && phoneNumber !== user.phoneNumber) {
          setError(PARTNER_MANAGER_PHONE_LOCKED);
          return;
        }
        setError(null);
        void userRepo
          .update(user.id, {
            name,
            phoneNumber: canEditPhone ? phoneNumber : user.phoneNumber,
            email: patch.email.trim(),
            partnerContactRole: patch.partnerContactRole.trim() || null,
          })
          .then(() => {
            if (savedId) return loadAppManagers(savedId);
          })
          .catch((error: unknown) => {
            const message = error instanceof Error ? error.message : "";
            setError(
              message.includes(PARTNER_MANAGER_PHONE_LOCKED)
                ? PARTNER_MANAGER_PHONE_LOCKED
                : "שמירת פרטי המנהל נכשלה. ייתכן שהטלפון כבר בשימוש",
            );
          });
      }}
    />
  );

  const opportunitiesBlock = (
    <PartnerOrganizationOpportunitiesPanel
      opportunitySearch={opportunitySearch}
      onOpportunitySearchChange={setOpportunitySearch}
      opportunityOptions={opportunityOptions}
      selectedOpportunity={selectedOpportunity}
      onSelectedOpportunityChange={setSelectedOpportunity}
      onLinkOpportunity={() => void linkOpportunity()}
      linkedOpportunities={linkedOpportunities}
      onUnlinkOpportunity={(opportunity) => void unlinkOpportunity(opportunity)}
    />
  );

  const isWizard = flow === "wizard";
  const showProfile = isWizard ? createStep === 0 : editTab === "profile";
  const title = savedId
    ? isWizard
      ? "ארגון שותף חדש"
      : "עריכת ארגון שותף"
    : "ארגון שותף חדש";
  const subtitle = isWizard
    ? createStep === 2
      ? "הארגון נשמר - אפשר לשייך מנהלים ומשימות"
      : "שם, כתובות וטיפול - מנהלים אחרי יצירה"
    : "פרופיל, כתובות, דירוג ומנהלים במקום אחד";

  const profileMount = (
    <Box sx={{ display: showProfile ? "block" : "none" }}>
      {isWizard ? (
        <Stack gap={2.5}>
          {profileBlock}
          {addressesBlock}
        </Stack>
      ) : (
        profileBlock
      )}
    </Box>
  );

  const activeContent = isWizard ? (
    <Stack gap={3}>
      <Stepper
        activeStep={createStep}
        alternativeLabel
        sx={{
          pt: 0.5,
          "& .MuiStepLabel-label": { fontSize: 13, fontWeight: 600 },
          "& .MuiStepLabel-label.Mui-active": { fontWeight: 700 },
        }}
      >
        {CREATE_STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {profileMount}
      {createStep === 1 ? handlingBlock : null}
      {createStep === 2 ? (
        <Stack gap={2.5}>
          {managersBlock}
          {opportunitiesBlock}
        </Stack>
      ) : null}
    </Stack>
  ) : (
    <>
      {profileMount}
      {editTab === "addresses" ? addressesBlock : null}
      {editTab === "handling" ? handlingBlock : null}
      {editTab === "managers" ? managersBlock : null}
      {editTab === "opportunities" ? opportunitiesBlock : null}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      dir="rtl"
      PaperProps={{
        sx: {
          width: { xs: "100%", md: 1120 },
          maxWidth: { xs: "100%", md: "calc(100% - 48px)" },
          height: { xs: "100%", md: "min(880px, 90vh)" },
          maxHeight: { xs: "100%", md: "90vh" },
          borderRadius: { xs: 0, md: 3 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 3.5,
          py: 2.25,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.75} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BusinessOutlinedIcon />
          </Box>
          <Stack gap={0.25} sx={{ minWidth: 0 }}>
            <Typography
              component="span"
              sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.25 }}
            >
              {title}
            </Typography>
            <Typography color="text.secondary" noWrap sx={{ fontSize: 14 }}>
              {subtitle}
            </Typography>
          </Stack>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="סגירה"
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {!isWizard ? (
          <Tabs
            orientation="vertical"
            value={editTab}
            onChange={(_, value: EditTab) => {
              void handleEditTabChange(value);
            }}
            variant="scrollable"
            sx={{
              width: { xs: "100%", md: 220 },
              flexShrink: 0,
              borderInlineEnd: { md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: "divider",
              px: 1.5,
              py: 2,
              bgcolor: "background.paper",
              "& .MuiTabs-indicator": {
                width: 3,
                borderRadius: 999,
                insetInlineStart: "auto",
              },
              "& .MuiTab-root": {
                alignItems: "center",
                justifyContent: "flex-start",
                textAlign: "start",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14.5,
                minHeight: 48,
                borderRadius: 2,
                mx: 0.25,
                my: 0.25,
                px: 1.5,
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                  bgcolor: "action.selected",
                  fontWeight: 700,
                },
              },
            }}
          >
            {EDIT_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  icon={<Icon sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                  label={tab.label}
                />
              );
            })}
          </Tabs>
        ) : null}
        <DialogContent
          sx={{
            flex: 1,
            px: { xs: 2, md: 3.5 },
            py: { xs: 2.5, md: 3 },
            bgcolor: "grey.50",
          }}
        >
          {activeContent}
        </DialogContent>
      </Box>
      {error ? (
        <Typography color="error" variant="body2" sx={{ px: 3.5, pt: 1.25, pb: 0 }}>
          {error}
        </Typography>
      ) : savedNotice ? (
        <Alert severity="success" sx={{ mx: 3.5, mt: 1.25 }}>
          {savedNotice}
        </Alert>
      ) : null}
      <DialogActions
        sx={{
          px: 3.5,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          gap: 1.25,
          bgcolor: "background.paper",
        }}
      >
        {isWizard && createStep === 0 ? (
          <>
            <Button
              onClick={onClose}
              disabled={saving}
              sx={{ textTransform: "none", px: 2.5, minHeight: 42 }}
            >
              ביטול
            </Button>
            <Button
              variant="contained"
              onClick={() => void goNextFromProfile()}
              disabled={!values.name.trim()}
              sx={{ textTransform: "none", px: 3, minHeight: 42, borderRadius: 2 }}
            >
              המשך
            </Button>
          </>
        ) : null}
        {isWizard && createStep === 1 ? (
          <>
            <Button
              onClick={() => setCreateStep(0)}
              disabled={saving}
              sx={{ textTransform: "none", px: 2.5, minHeight: 42 }}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSave("continue")}
              disabled={saving || !values.name.trim()}
              sx={{ textTransform: "none", px: 3, minHeight: 42, borderRadius: 2 }}
            >
              {saving ? "שומר..." : savedId ? "שמור והמשך" : "צור והמשך"}
            </Button>
          </>
        ) : null}
        {isWizard && createStep === 2 ? (
          <>
            <Button
              onClick={() => setCreateStep(1)}
              disabled={saving}
              sx={{ textTransform: "none", px: 2.5, minHeight: 42 }}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{ textTransform: "none", px: 3, minHeight: 42, borderRadius: 2 }}
            >
              סיום
            </Button>
          </>
        ) : null}
        {!isWizard ? (
          <>
            <Button
              onClick={onClose}
              disabled={saving}
              sx={{ textTransform: "none", px: 2.5, minHeight: 42 }}
            >
              ביטול
            </Button>
            {editTab === "profile" ||
            editTab === "addresses" ||
            editTab === "handling" ? (
              <Button
                variant="contained"
                onClick={() => void handleSave("stay")}
                disabled={saving}
                sx={{ textTransform: "none", px: 3, minHeight: 42, borderRadius: 2 }}
              >
                {saving ? "שומר..." : "שמירה"}
              </Button>
            ) : null}
          </>
        ) : null}
      </DialogActions>
      <MapPinDialog
        open={!!mapAddressClientId}
        title="בחירת מיקום ארגון"
        initialValue={
          addressDrafts.find((draft) => draft.clientId === mapAddressClientId)
            ?.coordinates ?? null
        }
        onClose={() => setMapAddressClientId(null)}
        onSave={(coords) => {
          if (!mapAddressClientId) return;
          updateAddressDrafts((current) =>
            current.map((draft) =>
              draft.clientId === mapAddressClientId
                ? {
                    ...draft,
                    coordinates: coords,
                    address: draft.address.trim() || "מיקום שנבחר במפה",
                  }
                : draft,
            ),
          );
          setMapAddressClientId(null);
        }}
      />
    </Dialog>
  );
}
